import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'

const ContaContext = createContext()

export function ContaProvider({ children }) {
  const [contas, setContas] = useState([])
  const [contaAtual, setContaAtual] = useState(null)
  const [saldo, setSaldo] = useState({
    saldo_disponivel: 0,
    saldo_bloqueado: 0,
    saldo_a_receber: 0,
    total_recebido: 0,
    total_sacado: 0
  })
  const [loading, setLoading] = useState(true)

  // Buscar contas (workspaces) do usuário
  const fetchContas = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .eq('owner_id', user.id)
        .eq('ativo', true)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true })

      if (error) throw error

      setContas(data || [])
      
      // Se não tem conta atual ou a atual não está na lista
      if (data && data.length > 0) {
        const savedContaId = localStorage.getItem('currentContaId')
        const found = data.find(c => c.id === savedContaId)
        
        if (found) {
          setContaAtual(found)
        } else {
          // Usar o default ou o primeiro
          const defaultConta = data.find(c => c.is_default) || data[0]
          setContaAtual(defaultConta)
          localStorage.setItem('currentContaId', defaultConta.id)
        }
      }
    } catch (error) {
      console.error('Erro ao buscar contas:', error)
    }
  }, [])

  // Buscar saldo da conta
  const fetchSaldo = useCallback(async (contaId) => {
    if (!contaId) return
    
    try {
      const { data, error } = await supabase
        .from('workspace_saldos')
        .select('*')
        .eq('workspace_id', contaId)
        .single()

      if (error && error.code !== 'PGRST116') throw error

      if (data) {
        setSaldo(data)
      }
    } catch (error) {
      console.error('Erro ao buscar saldo:', error)
    }
  }, [])

  // Carregar contas ao iniciar
  useEffect(() => {
    fetchContas().then(() => setLoading(false))
  }, [fetchContas])

  // Atualizar saldo quando mudar conta
  useEffect(() => {
    if (contaAtual?.id) {
      fetchSaldo(contaAtual.id)
    }
  }, [contaAtual?.id, fetchSaldo])

  // Trocar conta
  const trocarConta = useCallback((conta) => {
    setContaAtual(conta)
    localStorage.setItem('currentContaId', conta.id)
    // Recarregar a página para atualizar todos os dados
    window.location.reload()
  }, [])

  // Criar nova conta/subconta
  const criarConta = useCallback(async (contaData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('workspaces')
        .insert({
          ...contaData,
          owner_id: user.id
        })
        .select()
        .single()

      if (error) throw error

      await fetchContas()
      return data
    } catch (error) {
      console.error('Erro ao criar conta:', error)
      throw error
    }
  }, [fetchContas])

  // Atualizar conta
  const atualizarConta = useCallback(async (contaId, updates) => {
    try {
      const { data, error } = await supabase
        .from('workspaces')
        .update(updates)
        .eq('id', contaId)
        .select()
        .single()

      if (error) throw error

      await fetchContas()
      if (contaAtual?.id === contaId) {
        setContaAtual(data)
      }
      return data
    } catch (error) {
      console.error('Erro ao atualizar conta:', error)
      throw error
    }
  }, [fetchContas, contaAtual])

  // Definir conta padrão
  const setContaPadrao = useCallback(async (contaId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Usuário não autenticado')

      // Remover default de todas
      await supabase
        .from('workspaces')
        .update({ is_default: false })
        .eq('owner_id', user.id)

      // Definir novo default
      const { error } = await supabase
        .from('workspaces')
        .update({ is_default: true })
        .eq('id', contaId)

      if (error) throw error

      await fetchContas()
    } catch (error) {
      console.error('Erro ao definir conta padrão:', error)
      throw error
    }
  }, [fetchContas])

  // Realizar transferência (saque)
  const solicitarTransferencia = useCallback(async (transferData) => {
    try {
      const { data, error } = await supabase
        .from('transferencias')
        .insert({
          ...transferData,
          workspace_id: contaAtual.id
        })
        .select()
        .single()

      if (error) throw error
      
      // Atualizar saldo
      await fetchSaldo(contaAtual.id)
      
      return data
    } catch (error) {
      console.error('Erro ao solicitar transferência:', error)
      throw error
    }
  }, [contaAtual, fetchSaldo])

  const value = {
    contas,
    contaAtual,
    saldo,
    loading,
    trocarConta,
    criarConta,
    atualizarConta,
    setContaPadrao,
    solicitarTransferencia,
    recarregarContas: fetchContas,
    recarregarSaldo: () => fetchSaldo(contaAtual?.id)
  }

  return (
    <ContaContext.Provider value={value}>
      {children}
    </ContaContext.Provider>
  )
}

export function useConta() {
  const context = useContext(ContaContext)
  if (!context) {
    throw new Error('useConta deve ser usado dentro de ContaProvider')
  }
  return context
}
